import { Box, useTheme } from "@mui/material";
import { useCallback, useMemo } from "react";
import { Card, Typography, Link } from "@powerdiary/components-library/lib";
import { useClientPortalNavigate } from "../../../../../hooks/useClientPortalRouter";
import { ROUTES } from "../../../../../routes";
import { useClientPortalSelector, selectBooking } from "../../../../../redux";
import WaitlistIcon from "../WaitlistIcon";
import { useAnalytics } from "../../../../../hooks/useAnalytics";

const WAITLIST_TEXT =
    "Can't see a time that suits you? Request a place on our waitlist and we'll reach out when there's availability.";

export default function WaitlistCard() {
    const theme = useTheme();
    const navigateTo = useClientPortalNavigate();
    const { location, provider, service, session } = useClientPortalSelector(selectBooking);
    const { track } = useAnalytics();

    const waitlistUrl = useMemo(() => {
        const params = new URLSearchParams();

        const locationId = location.data?.locationId;
        const providerId = provider.data?.providerId;

        if (locationId) {
            params.append("locationId", locationId.toString());
        }

        if (providerId) {
            params.append("practitionerId", providerId.toString());
        }

        const queryString = params.toString();
        const baseUrl = ROUTES.WAITLIST_REQUEST.urlPath;
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }, [location.data?.locationId, provider.data?.providerId]);

    const handleWaitlistRequestClick = useCallback(() => {
        track("booking_waitlist_clicked", {
            booking_type: session.bookingType,
            location_id: location.data?.locationId,
            location_name: location.data?.locationName,
            provider_id: provider.data?.providerId,
            provider_name: provider.data?.providerName,
            service_id: service.data?.serviceId,
            service_name: service.data?.serviceName,
        });
        navigateTo(waitlistUrl);
    }, [navigateTo, waitlistUrl, session.bookingType, location.data, provider.data, service.data, track]);

    const cardStyles = useMemo(
        () => ({
            border: `1px solid ${theme.palette.primary.main}`,
            background: theme.palette.background.default,
        }),
        [theme.palette.primary.main, theme.palette.background.default]
    );

    return (
        <Card sx={cardStyles} data-test-id="waitlist_request_card">
            <Box display="flex" gap={1}>
                <Box sx={{ marginTop: "5px" }}>
                    <WaitlistIcon />
                </Box>

                <Box display="flex" flexDirection="column" gap={1}>
                    <Typography
                        variant="paragraphSmallRegular"
                        color={theme.palette.primary.main}
                        data-test-id="text_waitlist_request"
                    >
                        {WAITLIST_TEXT}
                    </Typography>

                    <Link
                        variant="small"
                        onClick={handleWaitlistRequestClick}
                        sx={{ textTransform: "uppercase" }}
                        data-test-id="button_waitlist_request"
                    >
                        Waitlist
                    </Link>
                </Box>
            </Box>
        </Card>
    );
}
